import React, { useState } from 'react'
import { useRouter } from 'next/router'
import { type SrpExperience } from '../utils/searchUtils'
import { AppCookies, useCookies } from '@src/utils/cookies'
import { Switch } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { FormattedMessage } from 'react-intl'
import { sendSrpExperienceToggleEvent } from '@src/utils/analytics'
import CircleLoader from './CircleLoader/CircleLoader'

const useStyles = makeStyles<{ currentExperience: SrpExperience }>()(
  (theme, { currentExperience }) => ({
    toggleContainer: {
      display: 'inline-flex',
      alignItems: 'center',
      marginRight: theme.spacing(2),
      '&:focus-visible': {
        outline: 'none',
      },
    },
    labelBase: {
      lineHeight: 1.25,
      padding: 0,
      margin: 0,
      color: theme.palette.text.primary,
    },
    legacyLabel: {
      fontWeight:
        currentExperience === 'old'
          ? theme.typography.fontWeightBold
          : theme.typography.fontWeightRegular,
      fontSize:
        currentExperience === 'old'
          ? theme.typography.pxToRem(14)
          : theme.typography.pxToRem(12),
    },
    newLabel: {
      fontWeight:
        currentExperience === 'new'
          ? theme.typography.fontWeightBold
          : theme.typography.fontWeightRegular,
      fontSize:
        currentExperience === 'new'
          ? theme.typography.pxToRem(14)
          : theme.typography.pxToRem(12),
    },
    switchBase: {
      color: theme.palette.primary.main,
      margin: 0,
      '&:focus': {
        outline: 'none',
      },
    },
    checked: {
      color: theme.palette.primary.main,
    },
    track: {
      backgroundColor: theme.palette.primary.main,
    },
    loadingMessage: {
      fontSize: theme.typography.pxToRem(22),
      fontWeight: theme.typography.fontWeightBold,
      lineHeight: 1.4,
      margin: '0 auto',
      textAlign: 'center',
      top: -500,
      width: 375,
      [theme.breakpoints.up('md')]: {
        fontSize: theme.typography.pxToRem(28),
        width: 600,
      },
    },
  })
)

interface SearchResultsToggleProps {
  currentExperience: SrpExperience
  className?: string
}

const SearchResultsToggle: React.FC<SearchResultsToggleProps> = ({
  currentExperience,
  className = '',
}) => {
  const router = useRouter()
  const [, setCookie] = useCookies([AppCookies.SrpExperience])
  const { classes, cx } = useStyles({ currentExperience })
  const [toggleClicked, setToggleClicked] = useState(false)

  const handleToggle = (): void => {
    const newExperience: SrpExperience =
      currentExperience === 'old' ? 'new' : 'old'

    const toggleDetail = `switch from ${currentExperience} version to ${newExperience} version`
    sendSrpExperienceToggleEvent(toggleDetail)
    setToggleClicked(true)
    setCookie(AppCookies.SrpExperience, newExperience, { path: '/' })
    router.reload()
  }

  const loadingMessage = (
    <FormattedMessage
      id="SRP_TOGGLE_LOADING_MESSAGE"
      defaultMessage={`Please wait while we take you to our ${currentExperience === 'old' ? 'new' : 'previous'} search experience.`}
    />
  )

  if (toggleClicked) {
    return (
      <CircleLoader
        message={loadingMessage}
        messageStyleOverride={classes.loadingMessage}
      />
    )
  }

  return (
    <div
      className={cx(classes.toggleContainer, className)}
      data-testid="search-results-toggle"
    >
      <span className={cx(classes.labelBase, classes.legacyLabel)}>
        <FormattedMessage id="PREVIOUS" defaultMessage="Previous" />
      </span>
      <Switch
        checked={currentExperience === 'new'}
        onChange={handleToggle}
        classes={{
          switchBase: classes.switchBase,
          checked: classes.checked,
          track: classes.track,
        }}
        sx={{
          '&:hover': {
            backgroundColor: 'transparent',
          },
          '&:focus': {
            outline: 'none',
          },
          '&:focus-visible': {
            outline: 'none',
          },
        }}
        inputProps={{ 'aria-label': 'Toggle search experience' }}
      />

      <span className={cx(classes.labelBase, classes.newLabel)}>
        <FormattedMessage id="TOGGLE_NEW" defaultMessage="New" />
      </span>
    </div>
  )
}

export default SearchResultsToggle
